import { useEffect, useState } from "react";
import { Reorder } from "framer-motion";
import { GripVertical, ChevronDown, ChevronUp, Eye, EyeOff, RotateCcw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { inputCls, fullUrl, canDo } from "./shared";
import { MediaPicker } from "./MediaPicker";

const SECTION_LABELS = {
  announcement: "Announcement Bar", hero: "Main Hero Banner", categories: "Shop by Category",
  featured: "Featured Collection", campaign: "Signature Campaign Banner", craftsmanship: "Craftsmanship & Fabric",
  colour_story: "Colour Story", denim: "Denim Edit Banner", brand_story: "Brand Story",
  journal: "Journal & Editorial", newsletter: "Newsletter",
};

const TEXT = "text", AREA = "area", IMAGE = "image";
const SECTION_FIELDS = {
  announcement: [
    { k: "text", label: "Message", t: TEXT }, { k: "link", label: "Link (optional)", t: TEXT }],
  hero: [
    { k: "image", label: "Desktop image", t: IMAGE }, { k: "image_mobile", label: "Mobile image (optional — used instead on phones)", t: IMAGE },
    { k: "overline", label: "Eyebrow text", t: TEXT }, { k: "heading1", label: "Heading line 1", t: TEXT },
    { k: "heading2", label: "Heading line 2 (italic)", t: TEXT }, { k: "subtext", label: "Subtext", t: AREA },
    { k: "cta1_label", label: "Button 1 label", t: TEXT }, { k: "cta1_link", label: "Button 1 link", t: TEXT },
    { k: "cta2_label", label: "Button 2 label", t: TEXT }, { k: "cta2_link", label: "Button 2 link", t: TEXT }],
  categories: [{ k: "overline", label: "Eyebrow", t: TEXT }, { k: "title", label: "Heading", t: TEXT }],
  featured: [{ k: "overline", label: "Eyebrow", t: TEXT }, { k: "title", label: "Heading", t: TEXT }],
  campaign: [
    { k: "image", label: "Banner image", t: IMAGE }, { k: "overline", label: "Eyebrow", t: TEXT },
    { k: "heading1", label: "Heading line 1", t: TEXT }, { k: "heading2", label: "Heading line 2 (italic)", t: TEXT },
    { k: "text", label: "Description", t: AREA }, { k: "cta_label", label: "Button label", t: TEXT }, { k: "cta_link", label: "Button link", t: TEXT }],
  colour_story: [{ k: "overline", label: "Eyebrow", t: TEXT }, { k: "title", label: "Heading", t: TEXT }],
  journal: [
    { k: "overline", label: "Eyebrow", t: TEXT }, { k: "title", label: "Heading", t: TEXT },
    { k: "image1", label: "Editorial image 1 (large)", t: IMAGE }, { k: "image2", label: "Editorial image 2 (small)", t: IMAGE }],
  newsletter: [
    { k: "overline", label: "Eyebrow", t: TEXT }, { k: "heading1", label: "Heading line 1", t: TEXT },
    { k: "heading2", label: "Heading line 2 (italic)", t: TEXT }, { k: "text", label: "Description", t: AREA }],
};
SECTION_FIELDS.craftsmanship = SECTION_FIELDS.campaign;
SECTION_FIELDS.denim = SECTION_FIELDS.campaign;
SECTION_FIELDS.brand_story = SECTION_FIELDS.campaign;

export function BuilderTab({ user }) {
  const [sections, setSections] = useState(null);
  const [meta, setMeta] = useState({});
  const [open, setOpen] = useState(null);
  const [pickerFor, setPickerFor] = useState(null); // {sectionId, key}
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const canEdit = canDo(user, "content", "edit");

  const load = () => api.get("/admin/content").then((r) => {
    setSections(r.data.draft?.sections || []);
    setMeta(r.data);
    setDirty(false);
  }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  if (!sections) return <p className="text-vv-muted text-sm py-10 animate-pulse">Loading website content…</p>;

  const update = (id, patch) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    setDirty(true);
  };
  const setField = (id, key, value) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, fields: { ...s.fields, [key]: value } } : s)));
    setDirty(true);
  };
  const reorder = (next) => { setSections(next); setDirty(true); };

  const saveDraft = async () => {
    setBusy(true);
    try {
      await api.put("/admin/content", { sections });
      toast.success("Draft saved — not live yet");
      setDirty(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      if (dirty) await api.put("/admin/content", { sections });
      await api.post("/admin/content/publish");
      toast.success("Published — live on the storefront");
      setDirty(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const restore = async () => {
    if (!window.confirm("Restore the previously published version as your draft? Current draft changes will be replaced.")) return;
    try {
      await api.post("/admin/content/restore", { index: 0 });
      toast.success("Previous published version restored to draft");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

  return (
    <div data-testid="builder-tab" className="max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="text-[11px] text-vv-muted uppercase tracking-[0.15em]">
          <p data-testid="builder-status">{dirty ? "Unsaved changes" : "Draft saved"} {meta.updated_at ? `· edited ${fmt(meta.updated_at)} by ${meta.updated_by || "—"}` : ""}</p>
          <p className="mt-1">Published {fmt(meta.published_at)} · {meta.revisions || 0} revision(s)</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a data-testid="builder-preview" href="/" target="_blank" rel="noreferrer" className="vv-btn-outline-dark !px-4 inline-flex items-center gap-2">
            <ExternalLink size={12} /> Preview Storefront
          </a>
          {canEdit && meta.revisions > 0 && (
            <button data-testid="builder-restore" onClick={restore} className="vv-btn-outline-dark !px-4"><RotateCcw size={12} /> Restore Previous</button>
          )}
          {canEdit && (
            <>
              <button data-testid="builder-save-draft" onClick={saveDraft} disabled={busy || !dirty} className="vv-btn-outline-dark !px-4 disabled:opacity-40">Save Draft</button>
              <button data-testid="builder-publish" onClick={publish} disabled={busy} className="vv-btn-dark !px-5">Publish</button>
            </>
          )}
        </div>
      </div>

      <p className="text-[11px] text-vv-muted mb-6">Drag sections to reorder the homepage. Toggle visibility, edit content, then Save Draft or Publish. The announcement bar updates the top strip site-wide.</p>

      <Reorder.Group axis="y" values={sections} onReorder={canEdit ? reorder : () => {}} className="space-y-2">
        {sections.map((s) => (
          <Reorder.Item key={s.id} value={s} data-testid={`builder-section-${s.id}`} dragListener={canEdit}
            className="border border-vv-black/10 bg-white select-none">
            <div className="flex items-center gap-3 px-4 py-3">
              <GripVertical size={15} className="text-vv-stone cursor-grab active:cursor-grabbing shrink-0" />
              <span className="text-sm font-medium flex-1">{SECTION_LABELS[s.type] || s.id}</span>
              {!s.enabled && <span className="text-[9px] uppercase tracking-widest text-vv-stone">Hidden</span>}
              {canEdit && (
                <button data-testid={`builder-toggle-${s.id}`} onClick={() => update(s.id, { enabled: !s.enabled })}
                  aria-label={s.enabled ? "Hide section" : "Show section"}
                  className={`p-1.5 transition-colors ${s.enabled ? "text-vv-forest" : "text-vv-stone"}`}>
                  {s.enabled ? <Eye size={15} strokeWidth={1.5} /> : <EyeOff size={15} strokeWidth={1.5} />}
                </button>
              )}
              <button data-testid={`builder-edit-${s.id}`} onClick={() => setOpen(open === s.id ? null : s.id)}
                className="text-[10px] uppercase tracking-[0.2em] border border-vv-black/20 px-3 py-1.5 hover:border-vv-black transition-colors inline-flex items-center gap-1.5">
                {canEdit ? "Edit" : "View"} {open === s.id ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
              </button>
            </div>
            {open === s.id && (
              <fieldset disabled={!canEdit} className="border-t border-vv-black/10 p-5 grid md:grid-cols-2 gap-4">
                {(SECTION_FIELDS[s.type] || []).map((spec) => (
                  <div key={spec.k} className={spec.t !== TEXT ? "md:col-span-2" : ""}>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-vv-muted">{spec.label}</label>
                    {spec.t === IMAGE ? (
                      <div className="flex items-center gap-3 mt-2">
                        {s.fields?.[spec.k] ? (
                          <img src={fullUrl(s.fields[spec.k])} alt="" className="w-16 h-16 object-cover bg-vv-elevated" />
                        ) : (
                          <div className="w-16 h-16 bg-vv-elevated flex items-center justify-center text-[9px] text-vv-stone uppercase tracking-widest">None</div>
                        )}
                        {canEdit && (
                          <div className="flex flex-col gap-2">
                            <button type="button" data-testid={`builder-image-${s.id}-${spec.k}`}
                              onClick={() => setPickerFor({ sectionId: s.id, key: spec.k })}
                              className="vv-btn-outline-dark !px-4 !py-2 text-[10px]">Choose from Media Library</button>
                            {s.fields?.[spec.k] && (
                              <button type="button" onClick={() => setField(s.id, spec.k, "")} className="text-[10px] uppercase tracking-widest text-vv-burgundy text-left">Remove image</button>
                            )}
                          </div>
                        )}
                      </div>
                    ) : spec.t === AREA ? (
                      <textarea data-testid={`builder-field-${s.id}-${spec.k}`} rows={3} value={s.fields?.[spec.k] || ""}
                        onChange={(e) => setField(s.id, spec.k, e.target.value)} className={`${inputCls} mt-2`} />
                    ) : (
                      <input data-testid={`builder-field-${s.id}-${spec.k}`} value={s.fields?.[spec.k] || ""}
                        onChange={(e) => setField(s.id, spec.k, e.target.value)} className={`${inputCls} mt-2`} />
                    )}
                  </div>
                ))}
              </fieldset>
            )}
          </Reorder.Item>
        ))}
      </Reorder.Group>

      {pickerFor && (
        <MediaPicker
          onClose={() => setPickerFor(null)}
          onSelect={(url) => { setField(pickerFor.sectionId, pickerFor.key, url); setPickerFor(null); }}
        />
      )}
    </div>
  );
}
